import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Gallery from './Gallery';
import LoadErrorScreen from './LoadErrorScreen';
import CardTitleRow from './CardTitleRow';
import './ItemPage.css';

const getPhotoUrl = (photo) => {
  if (!photo || typeof photo !== 'string') return null;
  const t = photo.trim();
  if (!t) return null;
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  if (t.startsWith('/')) return `${process.env.REACT_APP_SERVER_URL}${t}`;
  return `${process.env.REACT_APP_SERVER_URL}/${t}`;
};

const ProductPage = () => {
  const { productNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [addingCart, setAddingCart] = useState(false);

  useEffect(() => {
    if (!productNumber) return;
    setLoading(true);
    setLoadError(false);
    Promise.all([
      fetch(`${process.env.REACT_APP_SERVER_URL}/api/products/${productNumber}`).then((r) =>
        r.ok ? r.json() : null
      ),
      fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/public`).then((r) =>
        r.ok ? r.json() : []
      ),
    ])
      .then(([prod, branchList]) => {
        setProduct(prod);
        setBranches(Array.isArray(branchList) ? branchList : []);
      })
      .catch(() => {
        setLoadError(true);
        setProduct(null);
      })
      .finally(() => setLoading(false));
  }, [productNumber]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !product) return;
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        const has = (data.items || []).some(
          (i) => i.productId && String(i.productId._id || i.productId) === String(product._id)
        );
        setInCart(has);
      })
      .catch(() => {});
  }, [product]);

  const images = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const p of product?.photos || []) {
      const url = getPhotoUrl(p);
      if (url && !seen.has(url)) {
        seen.add(url);
        list.push(url);
      }
    }
    return list;
  }, [product?.photos]);

  const getBranchAddress = (branchId) => {
    const b = branches.find((x) => (x.branchId || x._id) === branchId);
    if (!b) return null;
    const parts = [b.city, b.address].filter(Boolean);
    return parts.length ? parts.join(', ') : b.branchId || b._id;
  };

  const handleAddToCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (window.confirm('Для добавления в корзину нужно войти. Перейти на страницу входа?')) {
        navigate('/login', { state: { redirectTo: `${location.pathname}${location.search}` } });
      }
      return;
    }
    if (addingCart || inCart) return;
    setAddingCart(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productNumber: product.productNumber, quantity: 1 }),
      });
      if (res.ok) setInCart(true);
    } catch (err) {
      console.error('Ошибка добавления в корзину:', err);
    } finally {
      setAddingCart(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  if (!product) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Товар не найден.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/products')} sx={{ mt: 2 }}>
          К каталогу товаров
        </Button>
      </Container>
    );
  }

  const av = product.branchAvailability || [];
  const inStock = av.filter((a) => Number(a.deliveryDays) === 0);
  const withDelivery = av.filter((a) => Number(a.deliveryDays) > 0);
  const inStockAddresses = inStock.map((a) => getBranchAddress(a.branchId)).filter(Boolean);
  const deliveryItems = withDelivery
    .map((a) => ({ address: getBranchAddress(a.branchId), days: a.deliveryDays }))
    .filter((x) => x.address);

  return (
    <Container sx={{ paddingTop: 4, paddingBottom: 4, backgroundColor: '#F5F3F0', minHeight: '100vh' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/products')}
        sx={{ color: '#8B7D6B', mb: 2 }}
      >
        К каталогу товаров
      </Button>

      <Box className="item-detail-layout">
        <Box className="item-detail-gallery" sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 3,
              backgroundColor: '#2e7d32',
              color: '#fff',
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            Продажа
          </Box>
          {images.length > 0 ? (
            <Gallery images={images} />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: 300,
                bgcolor: 'grey.200',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="text.secondary">Нет фото</Typography>
            </Box>
          )}
        </Box>

        <Box className="item-detail-content item-description" sx={{ mt: { xs: 3, md: 0 }, mb: 3 }}>
        <CardTitleRow
          type="product"
          entityId={product._id}
          titleVariant="h4"
          titleClassName="item-title"
          gutterBottom
          titleSx={{ color: '#000000', fontWeight: 'bold' }}
        >
          {product.title}
        </CardTitleRow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {product.productNumber}
          </Typography>
          <Chip
            size="small"
            label={product.condition === 'used' ? 'Б/у' : 'Новый'}
            sx={{
              backgroundColor: product.condition === 'used' ? '#757575' : '#2e7d32',
              color: '#fff',
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography variant="h5" sx={{ mt: 2, mb: 2, color: '#8B7D6B', fontWeight: 'bold' }}>
          {product.price != null
            ? `${Number(product.price).toLocaleString('ru-RU')} ₽`
            : 'Цена по запросу'}
        </Typography>
        <Typography variant="body1" sx={{ color: '#000000', lineHeight: 1.8, mb: 3, whiteSpace: 'pre-wrap' }}>
          {product.description}
        </Typography>

        {inStockAddresses.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              В наличии:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
              {inStockAddresses.map((addr, i) => (
                <li key={i}>
                  <Typography variant="body2">{addr}</Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}

        {deliveryItems.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Сроки доставки
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
              {deliveryItems.map((item, i) => (
                <li key={i}>
                  <Typography variant="body2">
                    {item.address} — {item.days}{' '}
                    {item.days === 1 ? 'день' : item.days < 5 ? 'дня' : 'дней'}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}

        <Button
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          onClick={handleAddToCart}
          disabled={addingCart || inCart}
          fullWidth
          sx={{
            mt: 2,
            mb: 2,
            backgroundColor: '#8B7D6B',
            '&:hover': { backgroundColor: '#6B5D4B' },
          }}
        >
          {inCart ? 'В корзине' : addingCart ? 'Добавляем...' : 'В корзину'}
        </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default ProductPage;
